import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faCircleCheck, faGraduationCap } from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import { useAcademyAccess } from '../../utils/academyAccess';
import '../../styles/academy.css';

/**
 * Me > Academy: the employee's GTS Academy (Moodle) courses.
 *
 * Not joined: only a Join Academy button, which creates their account. Joined:
 * their courses, and Go to Academy, which logs them straight in. Their email already
 * on another account, such as the admin: a link to Academy's login page. If Academy cannot
 * answer the page says so and offers no button, so nobody is asked to join when
 * they already have. All of it goes through /api/academy (server/routes/academy.js).
 */
const errorText = (err) => err.response?.data?.message || 'Something went wrong. Please try again.';

const Academy = () => {
    const access = useAcademyAccess();
    const [state, setState] = useState({ phase: 'loading', courses: [], viewAs: false });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setError('');
        setState(s => ({ ...s, phase: 'loading' }));
        try {
            const { data } = await api.get('/academy/status');
            setState({
                phase: data.joined ? 'joined' : data.loginUrl ? 'existing' : 'new',
                courses: data.courses || [],
                loginUrl: data.loginUrl,
                viewAs: Boolean(data.viewAs)
            });
        } catch (err) {
            setState({ phase: 'unavailable', courses: [], viewAs: false, message: errorText(err) });
        }
    }, []);

    useEffect(() => { if (access.enabled) load(); }, [access.enabled, load]);

    const join = async () => {
        setBusy(true);
        setError('');
        try {
            await api.post('/academy/join');
            await load();
        } catch (err) {
            // An account turned out to use their email: show them the way in rather than an error.
            if (err.response?.status === 409) await load(); else setError(errorText(err));
        } finally {
            setBusy(false);
        }
    };

    const go = async (courseId) => {
        setBusy(true);
        setError('');
        // Opened before the request: a browser only allows a new tab straight after the click.
        const tab = window.open('', '_blank');
        try {
            const { data } = await api.post('/academy/go', courseId ? { courseId } : {});
            if (tab) tab.location.href = data.url; else window.location.assign(data.url);
        } catch (err) {
            if (tab) tab.close();
            setError(errorText(err));
        } finally {
            setBusy(false);
        }
    };

    if (access.loaded && !access.enabled) return <Navigate to="/dashboard" replace />;

    const { phase, courses, viewAs } = state;
    const canAct = !busy && !viewAs;

    return (
        <div className="settings-container fade-in">
            <div className="page-header-row mb-20 ak-header">
                <div>
                    <h1 className="page-title header-no-margin">
                        <FontAwesomeIcon icon={faGraduationCap} className="btn-icon" /> Academy
                    </h1>
                    <p className="ak-sub">Your GTS learning courses.</p>
                </div>
                {phase === 'joined' && (
                    <button type="button" className="gts-btn primary" onClick={() => go()} disabled={!canAct}>
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="btn-icon" /> Go to Academy
                    </button>
                )}
            </div>

            {error && <div className="ak-error" role="alert">{error}</div>}
            {viewAs && <div className="ak-note">You are viewing as someone else, so joining and opening Academy are off.</div>}

            {phase === 'loading' && <div className="ak-card ak-empty">Loading...</div>}

            {phase === 'unavailable' && (
                <div className="ak-card ak-empty">
                    <p>{state.message}</p>
                    <button type="button" className="gts-btn" onClick={load}>Try again</button>
                </div>
            )}

            {phase === 'new' && (
                <div className="ak-card ak-join">
                    <FontAwesomeIcon icon={faGraduationCap} className="ak-join-icon" />
                    <h2 className="ak-h2">Join GTS Academy</h2>
                    <p className="ak-sub">Your account is created with your portal details. There is no form to fill in.</p>
                    <button type="button" className="gts-btn primary" onClick={join} disabled={!canAct}>
                        {busy ? 'Joining...' : 'Join Academy'}
                    </button>
                </div>
            )}

            {phase === 'existing' && (
                <div className="ak-card ak-join">
                    <FontAwesomeIcon icon={faGraduationCap} className="ak-join-icon" />
                    <h2 className="ak-h2">You already have an Academy account</h2>
                    <p className="ak-sub">Log in with your Academy email and password.</p>
                    <a className="gts-btn primary" href={state.loginUrl} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="btn-icon" /> Go to Academy
                    </a>
                </div>
            )}

            {phase === 'joined' && courses.length === 0 && (
                <div className="ak-card ak-empty">You are on Academy, but not enrolled in any courses yet.</div>
            )}

            {phase === 'joined' && courses.length > 0 && (
                <ul className="ak-courses">
                    {courses.map(course => (
                        <li key={course.id} className="ak-card ak-course">
                            <div className="ak-course-main">
                                <h2 className="ak-h2">{course.name}</h2>
                                {course.completed ? (
                                    <span className="ak-done"><FontAwesomeIcon icon={faCircleCheck} /> Completed</span>
                                ) : course.progress !== null && (
                                    <div className="ak-progress">
                                        <div className="ak-bar" role="progressbar" aria-valuenow={course.progress} aria-valuemin={0} aria-valuemax={100} aria-label={`${course.name} progress`}>
                                            <span style={{ width: `${course.progress}%` }} />
                                        </div>
                                        <span className="ak-percent">{course.progress}%</span>
                                    </div>
                                )}
                            </div>
                            <button type="button" className="gts-btn" onClick={() => go(course.id)} disabled={!canAct}>Open</button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default Academy;
